<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="44" class="mr-3">
              <v-icon color="white">mdi-shield-account</v-icon>
            </v-avatar>
            <div>
              <div class="text-h5 font-weight-bold">{{ $t('role.title.role') }}</div>
              <div class="text-body-2 text--secondary">{{ $t('role.dashboard.manage_all_roles') }}</div>
            </div>
          </div>
          <div class="d-flex flex-wrap" style="gap: 8px">
            <v-btn text small :to="{ name: 'user-role-manager' }" v-can="'role.assign'">
              <v-icon left small>mdi-account-switch</v-icon>
              {{ $t('role.button.user_role_manager') }}
            </v-btn>
            <v-btn text small @click="openCreatePermission" v-can="'permission.create'">
              <v-icon left small>mdi-security-network</v-icon>
              {{ $t('permission.button.create') }}
            </v-btn>
            <v-btn color="primary" small @click="openCreate" v-can="'role.create'">
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('role.button.create') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Roles Table -->
    <v-card elevation="1">
      <v-card-title class="py-3 px-4">
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('text.search_placeholder')"
          single-line hide-details clearable dense outlined
          style="max-width: 280px"
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="roles"
        :search="search"
        :loading="loading"
        :items-per-page="15"
        :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
      >
        <template slot="item.name" slot-scope="{ item }">
          <div class="d-flex align-center">
            <v-avatar :color="roleColor(item.name)" size="28" class="mr-2">
              <v-icon color="white" size="14">mdi-shield</v-icon>
            </v-avatar>
            <span class="font-weight-medium">{{ item.name }}</span>
            <v-chip v-if="item.system" x-small outlined color="grey" class="ml-2">
              {{ $t('role.label.system') }}
            </v-chip>
          </div>
        </template>

        <template slot="item.guard_name" slot-scope="{ item }">
          <v-chip x-small outlined :color="item.guard_name === 'api' ? 'primary' : 'secondary'">
            {{ item.guard_name }}
          </v-chip>
        </template>

        <template slot="item.users_count" slot-scope="{ item }">
          <v-chip x-small outlined color="info">{{ item.users_count || 0 }}</v-chip>
        </template>

        <template slot="item.permissions_count" slot-scope="{ item }">
          <v-chip x-small outlined color="success">{{ item.permissions_count || 0 }}</v-chip>
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn icon small color="primary" v-on="on" @click="openEdit(item)" v-can="'role.update'">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('role.button.edit_role') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn icon small color="warning" v-on="on" @click="openAssign(item)" v-can="'permission.edit'">
                <v-icon small>mdi-key</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('role.button.assign_permission') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn icon small color="error" v-on="on" @click="confirmDelete(item)" v-can="'role.delete'" :disabled="!!item.system">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('role.button.delete_role') }}</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon color="grey lighten-1" size="56">mdi-shield-off</v-icon>
            <p class="text--secondary mt-2">{{ $t('role.dashboard.no_roles') }}</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="formDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4">
          {{ editingRole.id ? $t('role.button.edit_role') : $t('role.button.create') }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 pb-0">
          <v-form ref="roleForm" v-model="formValid">
            <v-text-field
              v-model="editingRole.name"
              :label="$t('role.label.name')"
              :rules="nameRules"
              outlined dense required autofocus class="mb-2"
            />
            <v-textarea
              v-model="editingRole.description"
              :label="$t('role.label.description')"
              outlined dense rows="3" class="mb-2"
            />
            <v-select
              v-model="editingRole.guard_name"
              :label="$t('role.label.guard_name')"
              :items="guardOptions"
              :rules="[v => !!v || 'Required']"
              outlined dense required
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="closeForm">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveRole" :loading="saving" :disabled="!formValid">
            {{ $t('button.save_changes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="360">
      <v-card>
        <v-card-title class="text-h6">{{ $t('role.confirm.delete') }}</v-card-title>
        <v-card-text>
          {{ $t('role.confirm.delete_text', { name: deletingRole && deletingRole.name }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="error" @click="executeDelete" :loading="loading">{{ $t('button.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Permissions Dialog -->
    <v-dialog v-model="assignDialog" max-width="640" persistent scrollable>
      <v-card>
        <v-card-title class="text-h6 pa-4">
          <v-icon color="warning" class="mr-2">mdi-key</v-icon>
          {{ $t('role.button.assign_permission') }}: <em class="ml-1">{{ assigningRole.name }}</em>
        </v-card-title>
        <v-divider />
        <v-card-subtitle class="pt-3 pb-2">
          <v-text-field
            v-model="permSearch"
            :placeholder="$t('text.search_placeholder')"
            prepend-inner-icon="mdi-magnify"
            clearable dense outlined hide-details
          />
        </v-card-subtitle>
        <v-card-text style="max-height: 460px; overflow-y: auto;" class="pa-0">
          <v-progress-linear v-if="loadingPerms" indeterminate />
          <v-expansion-panels v-else accordion flat>
            <v-expansion-panel v-for="cat in filteredPermCategories" :key="cat.name">
              <v-expansion-panel-header class="py-2">
                <div class="d-flex align-center">
                  <span class="font-weight-medium text-capitalize">{{ cat.name }}</span>
                  <v-chip x-small outlined color="grey" class="ml-2">{{ cat.permissions.length }}</v-chip>
                  <v-chip v-if="selectedCount(cat) > 0" x-small outlined color="success" class="ml-1">
                    {{ selectedCount(cat) }}
                  </v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="6" sm="4" v-for="perm in cat.permissions" :key="perm.id">
                    <v-checkbox
                      v-model="assignedPerms"
                      :value="perm.id"
                      :label="perm.name.split('.').slice(1).join('.')"
                      dense hide-details class="mt-0"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <span class="text-caption text--secondary">
            {{ assignedPerms.length }} {{ $t('permission.label.permissions') }}
          </span>
          <v-spacer />
          <v-btn text @click="closeAssign">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveAssign" :loading="saving">{{ $t('button.save_changes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Permission Dialog -->
    <v-dialog v-model="permDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4">{{ $t('permission.title.create') }}</v-card-title>
        <v-divider />
        <v-card-text class="pt-4 pb-0">
          <v-form ref="permForm" v-model="permFormValid">
            <v-text-field
              v-model="newPerm.name"
              :label="$t('permission.label.name')"
              :hint="$t('permission.label.name_placeholder')"
              persistent-hint
              :rules="permNameRules"
              outlined dense required autofocus class="mb-2"
            />
            <v-textarea
              v-model="newPerm.description"
              :label="$t('permission.label.description')"
              outlined dense rows="2" class="mb-2"
            />
            <v-select
              v-model="newPerm.guard_name"
              :label="$t('permission.label.guard_name')"
              :items="guardOptions"
              outlined dense
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="closeCreatePermission">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" @click="savePermission" :loading="savingPerm" :disabled="!permFormValid">
            {{ $t('button.save_changes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'RolesIndex',
  data () {
    return {
      roles: [],
      loading: false,
      search: '',
      // create / edit
      formDialog: false,
      formValid: false,
      editingRole: { name: '', description: '', guard_name: 'api' },
      saving: false,
      guardOptions: [{ text: 'API', value: 'api' }, { text: 'Web', value: 'web' }],
      // delete
      deleteDialog: false,
      deletingRole: null,
      // assign permissions
      assignDialog: false,
      assigningRole: {},
      allPermissions: [],
      assignedPerms: [],
      loadingPerms: false,
      permSearch: '',
      // create permission
      permDialog: false,
      permFormValid: false,
      newPerm: { name: '', description: '', guard_name: 'api' },
      savingPerm: false,
      snackbar: { show: false, text: '', color: 'success' }
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('role.label.name'), value: 'name' },
        { text: this.$t('role.label.guard_name'), value: 'guard_name', width: '110px' },
        { text: this.$t('role.label.users'), value: 'users_count', width: '90px', align: 'center' },
        { text: this.$t('permission.label.permissions'), value: 'permissions_count', width: '110px', align: 'center' },
        { text: this.$t('label.actions'), value: 'actions', sortable: false, width: '120px', align: 'center' }
      ]
    },
    nameRules () {
      return [
        v => !!v || this.$t('role.validation.name.required'),
        v => (v && v.length >= 3) || this.$t('role.validation.name.length')
      ]
    },
    permNameRules () {
      return [
        v => !!v || this.$t('permission.validation.name.required'),
        v => (v && v.length >= 3) || this.$t('permission.validation.name.length')
      ]
    },
    permCategories () {
      const cats = {}
      this.allPermissions.forEach(p => {
        const key = p.name.split('.')[0]
        if (!cats[key]) cats[key] = { name: key, permissions: [] }
        cats[key].permissions.push(p)
      })
      return Object.values(cats).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredPermCategories () {
      if (!this.permSearch) return this.permCategories
      const q = this.permSearch.toLowerCase()
      return this.permCategories
        .map(cat => ({ ...cat, permissions: cat.permissions.filter(p => p.name.toLowerCase().includes(q)) }))
        .filter(cat => cat.permissions.length > 0 || cat.name.includes(q))
    }
  },
  methods: {
    roleColor (name) {
      const palette = ['primary', 'success', 'warning', 'info', 'error', 'purple', 'indigo']
      return palette[name.toLowerCase().charCodeAt(0) % palette.length]
    },
    selectedCount (cat) {
      return cat.permissions.filter(p => this.assignedPerms.includes(p.id)).length
    },
    openCreate () {
      this.editingRole = { name: '', description: '', guard_name: 'api' }
      this.formDialog = true
      this.$nextTick(() => this.$refs.roleForm?.resetValidation())
    },
    openEdit (role) {
      this.editingRole = { ...role }
      this.formDialog = true
    },
    closeForm () {
      this.formDialog = false
    },
    async saveRole () {
      if (!this.$refs.roleForm.validate()) return
      this.saving = true
      try {
        if (this.editingRole.id) {
          const res = await this.$roleService.updateRole(this.editingRole.id, this.editingRole)
          const idx = this.roles.findIndex(r => r.id === this.editingRole.id)
          if (idx !== -1) this.roles.splice(idx, 1, res.data || this.editingRole)
          this.notify(this.$t('role.message.updated'))
        } else {
          await this.$roleService.createRole(this.editingRole)
          this.notify(this.$t('role.message.created'))
          await this.loadRoles()
        }
        this.closeForm()
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    confirmDelete (role) {
      this.deletingRole = role
      this.deleteDialog = true
    },
    async executeDelete () {
      this.loading = true
      try {
        await this.$roleService.deleteRole(this.deletingRole.id)
        this.roles = this.roles.filter(r => r.id !== this.deletingRole.id)
        this.notify(this.$t('role.message.deleted'))
        this.deleteDialog = false
      } catch (e) {
        this.globalErrHandle(e)
      } finally {
        this.loading = false
      }
    },
    async openAssign (role) {
      this.assigningRole = role
      this.assignDialog = true
      this.loadingPerms = true
      try {
        const [perms, rolePerms] = await Promise.all([
          this.$permissionService.getPermissions(),
          this.$roleService.getRolePermissions(role.id)
        ])
        this.allPermissions = perms
        this.assignedPerms = rolePerms.map(p => p.id)
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingPerms = false
      }
    },
    closeAssign () {
      this.assignDialog = false
      this.assigningRole = {}
      this.assignedPerms = []
      this.permSearch = ''
    },
    async saveAssign () {
      this.saving = true
      try {
        await this.$roleService.assignPermissions(this.assigningRole.id, { permissions: this.assignedPerms })
        this.notify(this.$t('role.message.permission_assigned'))
        await this.loadRoles()
        this.closeAssign()
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    openCreatePermission () {
      this.newPerm = { name: '', description: '', guard_name: 'api' }
      this.permDialog = true
      this.$nextTick(() => this.$refs.permForm?.resetValidation())
    },
    closeCreatePermission () {
      this.permDialog = false
    },
    async savePermission () {
      if (!this.$refs.permForm.validate()) return
      this.savingPerm = true
      try {
        await this.$permissionService.createPermission(this.newPerm)
        this.notify(this.$t('role.message.permission_created'))
        this.closeCreatePermission()
      } catch (e) {
        console.error(e)
      } finally {
        this.savingPerm = false
      }
    },
    notify (text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },
    async loadRoles () {
      this.loading = true
      try {
        this.roles = await this.$roleService.getRoles()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.$emit('parent-title', this.$t('role.title.role'))
    this.loadRoles()
  }
}
</script>
